import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createBlock as _createBlock, Fragment as _Fragment, renderList as _renderList, unref as _unref } from "vue"

const _hoisted_1 = [".open"]
const _hoisted_2 = { class: "modal-content" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = ["value", "checked"]
const _hoisted_5 = ["value", "checked"]
const _hoisted_6 = { key: 2 }
const _hoisted_7 = { slot: "footer" }
const _hoisted_8 = ["disabled"]

import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { epinioExceptionToErrorsArray } from '../utils/errors';
import Banner from '@components/Banner/Banner.vue';

const LARGE_BATCH_WARNING_THRESHOLD = 25;
const MAX_VISIBLE_NAMES = 5;


export default /*@__PURE__*/_defineComponent({
  __name: 'BulkDeleteModal',
  props: {
    resourceLabel: {},
    resourceType: {},
    bulkRemove: {},
    isDeletingApps: { type: Boolean, default: false },
    showUnbindNotice: { type: Boolean, default: false }
  },
  emits: ['deleted', 'settled'],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

// 'deleted' fires only on success (for growl/close-modal timing).
// 'settled' always fires, success or failure: batch deletes can partially
// succeed even when they ultimately error, so consumers whose list doesn't
// refresh via the generic findAll below (e.g. Applications, which renders
// from per-namespace fetches) need a signal that fires regardless of outcome.
const emit = __emit;

const store = useStore() as any;
const t = store.getters['i18n/t'];

const showModal = ref(false);
const itemsToDelete = ref<any[]>([]);
const deleting = ref(false);
const errors = ref<string[]>([]);
const deleteFromRegistry = ref(false);
const deletePVC = ref(false);

const names = computed(() => itemsToDelete.value.map((item) => item.meta?.name ?? item.name));
const visibleNames = computed(() => names.value.slice(0, MAX_VISIBLE_NAMES));
const remainingCount = computed(() => names.value.length - visibleNames.value.length);
const isLargeBatch = computed(() => itemsToDelete.value.length >= LARGE_BATCH_WARNING_THRESHOLD);
const resourceLabelPlural = computed(() => `${ props.resourceLabel }s`);

function openDelete(items: any[]) {
  itemsToDelete.value = items;
  errors.value = [];
  deleteFromRegistry.value = false;
  deletePVC.value = false;
  showModal.value = true;
}

function closeDelete() {
  showModal.value = false;
  itemsToDelete.value = [];
  errors.value = [];
  deleteFromRegistry.value = false;
  deletePVC.value = false;
}

async function onSubmitDelete() {
  if (!itemsToDelete.value.length) {
    return;
  }

  deleting.value = true;
  errors.value = [];
  const items = itemsToDelete.value;
  const count = items.length;
  const label = count === 1 ? props.resourceLabel : resourceLabelPlural.value;

  try {
    await props.bulkRemove(items, deleteFromRegistry.value, deletePVC.value);
    emit('deleted', items);
    closeDelete();
    store.dispatch('growl/success', {
      title:   `${ count } ${ label } Deleted`,
      message: `${ count } ${ label } successfully deleted.`,
    });
  } catch (e: any) {
    errors.value = epinioExceptionToErrorsArray(e);
    store.dispatch('growl/error', {
      title:   `${ label } Deletion Failed`,
      message: `Failed to delete one or more ${ label }. Please check the list and try again.`,
    });
  } finally {
    // A batch delete can partially succeed even when it ultimately errors,
    // so always refresh rather than only refreshing on success.
    await store.dispatch('epinio/refreshList', { type: props.resourceType });
    emit('settled', items);
    deleting.value = false;
  }
}

__expose({ openDelete });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("trailhand-modal", {
    ".open": showModal.value,
    title: "Are you sure?",
    onModalClose: closeDelete
  }, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("p", null, [
        _createTextVNode("\n        You are attempting to delete " + _toDisplayString(itemsToDelete.value.length) + " " + _toDisplayString(itemsToDelete.value.length === 1 ? _ctx.resourceLabel : resourceLabelPlural.value) + ":\n        ", 1),
        _createElementVNode("strong", null, _toDisplayString(visibleNames.value.join(', ')), 1),
        (remainingCount.value > 0)
          ? (_openBlock(), _createElementBlock("span", _hoisted_3, " and " + _toDisplayString(remainingCount.value) + " other" + _toDisplayString(remainingCount.value === 1 ? '' : 's'), 1))
          : _createCommentVNode("", true),
        _cache[2] || (_cache[2] = _createTextVNode(".\n      ", -1))
      ]),
      _cache[4] || (_cache[4] = _createTextVNode()),
      (isLargeBatch.value)
        ? (_openBlock(), _createBlock(Banner, {
            key: 0,
            color: "warning",
            label: `You're deleting ${itemsToDelete.value.length} ${resourceLabelPlural.value} — that's a lot. Make sure this is what you want.`
          }, null, 8, ["label"]))
        : _createCommentVNode("", true),
      _cache[5] || (_cache[5] = _createTextVNode()),
      (_ctx.isDeletingApps)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createElementVNode("trailhand-checkbox", {
              value: deleteFromRegistry.value,
              checked: deleteFromRegistry.value,
              onCheckboxChange: _cache[0] || (_cache[0] = (e) => { deleteFromRegistry.value = e.detail.checked; })
            }, "Remove the application's container image from registry", 40, _hoisted_4),
            _cache[3] || (_cache[3] = _createTextVNode()),
            _createElementVNode("trailhand-checkbox", {
              value: deletePVC.value,
              checked: deletePVC.value,
              onCheckboxChange: _cache[1] || (_cache[1] = (e) => { deletePVC.value = e.detail.checked; })
            }, "Delete the application's persistent storage", 40, _hoisted_5)
          ], 64))
        : _createCommentVNode("", true),
      _cache[6] || (_cache[6] = _createTextVNode()),
      (_ctx.showUnbindNotice)
        ? (_openBlock(), _createElementBlock("p", _hoisted_6, "\n        Any bound applications will be automatically unbound before deletion.\n      "))
        : _createCommentVNode("", true),
      _cache[7] || (_cache[7] = _createTextVNode()),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errors.value, (err, i) => {
        return (_openBlock(), _createBlock(Banner, {
          key: i,
          color: "error",
          label: err
        }, null, 8, ["label"]))
      }), 128))
    ]),
    _cache[9] || (_cache[9] = _createTextVNode()),
    _createElementVNode("div", _hoisted_7, [
      _createElementVNode("trailhand-button", {
        variant: "secondary",
        class: "mr-10",
        onButtonClick: closeDelete
      }, "\n        Cancel\n      ", 32),
      _cache[8] || (_cache[8] = _createTextVNode()),
      _createElementVNode("trailhand-button", {
        variant: "destructive",
        disabled: deleting.value,
        onButtonClick: onSubmitDelete
      }, _toDisplayString(deleting.value ? _unref(t)('generic.deleting') : _unref(t)('generic.delete')), 41, _hoisted_8)
    ])
  ], 40, _hoisted_1))
}
}

})